import type { PaymentPreference } from '@/types/brand';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3002';

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
}

async function api<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const { method = 'GET', body } = options;
  const url = `${API_BASE_URL}${endpoint}`;

  const fetchOptions: RequestInit = {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  };

  if (body && method !== 'GET') {
    fetchOptions.body = JSON.stringify(body);
  }

  const response = await fetch(url, fetchOptions);

  if (response.status === 401 || response.status === 403) {
    window.dispatchEvent(new CustomEvent('auth:unauthorized', { detail: { status: response.status } }));
    throw new Error(`Unauthorized (${response.status})`);
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`API error (${response.status}): ${errorText}`);
  }

  return response.json();
}

interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

export type DiscountType = 'CART_PERCENT' | 'CART_FIXED' | 'ITEM_DISCOUNT' | 'ITEM_QUANTITY_TIERS';
/** `UNSUPPORTED` is POS-only: the POS is running the discount on a condition Menteo cannot honour. */
export type DiscountStatus = 'ACTIVE' | 'DISABLED' | 'UNSUPPORTED';

/** Who authored the discount — and therefore whether the merchant may edit it here. */
export type DiscountSource = 'MERCHANT' | 'POS';

/** Empty/null on a coupon means both fulfilment types qualify. */
export type DiscountDeliveryType = 'delivery' | 'pickup';

export type DiscountDayOfWeek = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

export interface DiscountTimeRange {
  /** 'HH:mm', 24h. */
  open: string;
  close: string;
}

/**
 * Weekly recurrence windows, same shape as the backend's `OperatingHours` (locations domain).
 * A missing day means the promotion never runs that day; no range may cross midnight — split it
 * into two days instead, exactly as the location schedules do.
 */
export type DiscountScheduleHours = Partial<Record<DiscountDayOfWeek, DiscountTimeRange[]>>;

export interface DiscountRewardItem {
  catalogItemId: string;
  /** Units the discount reaches. A cart holding more keeps the excess at full price. */
  quantity: number;
  /** 100 = free. */
  discountPercent: number;
}

/**
 * How a quantity ladder turns units bought into units discounted. Picked once per promotion — the
 * two are different deals, not two ways of writing the same one.
 *
 * With a 2→10%, 3→20% ladder and 5 units in the cart: `THRESHOLD` gives 20% on all five,
 * `BUNDLE` gives 20% on three plus 10% on two, and at 4 units `BUNDLE` leaves the 4th at full price.
 */
export type QuantityTierMode = 'THRESHOLD' | 'BUNDLE';

/** One rung: buy this many of the promotion's items, get this percentage off each of them. */
export interface DiscountQuantityTier {
  /** At least 2 — a 1-unit rung would be an unconditional item discount. */
  quantity: number;
  /** Percentage off each covered unit's full price, modifiers included. */
  discountPercent: number;
}

/**
 * A "2x" promotion. Units are POOLED across `catalogItemIds` — two different pizzas reach the 2-unit
 * rung together — and in `BUNDLE` mode the dearest units are packed first.
 *
 * Automatic promotions only: the API refuses a ladder on a coded coupon, because the deal is earned
 * by what is in the cart rather than by typing anything.
 */
export interface DiscountQuantityTiers {
  mode: QuantityTierMode;
  catalogItemIds: string[];
  tiers: DiscountQuantityTier[];
}

export interface Discount {
  id: string;
  brandId: string;
  /**
   * Merchant-authored, or pulled from the brand's POS. A `POS` row is written only by the menu sync
   * and is READ-ONLY here: an edit would be reverted by the next sync.
   */
  source: DiscountSource;
  /** The POS's own id for the discount (Rhino's `descuento_id`); null on merchant-authored rows. */
  posExternalId?: string | null;
  /**
   * POS rows only: the synced menu prices are already net of this discount, so nothing applies it to
   * a cart — it exists so the order push can name it and stop the POS re-pricing at gross.
   */
  reflectedInPrices?: boolean;
  /**
   * Null marks an *automatic promotion*: a codeless discount the agent attaches on its own whenever
   * the conditions below hold. Everything else about the entity is identical, which is why the three
   * flavours share this type — the Discounts hub splits them client-side on `code` and `source`.
   */
  code: string | null;
  /** Display name. Required for codeless promotions (there is no code to call them by). */
  name?: string | null;
  description?: string | null;
  type: DiscountType;
  value: number;
  rewardItems?: DiscountRewardItem[] | null;
  /** The ladder of an `ITEM_QUANTITY_TIERS` promotion; null for every other type. */
  quantityTiers?: DiscountQuantityTiers | null;
  /** Checked only when the customer confirms, never when they type the code. */
  minimumOrderAmount?: number | null;
  startsAt?: string | null;
  endsAt?: string | null;
  excludePromoItems: boolean;
  maxUsesPerCustomer?: number | null;
  maxUsesTotal?: number | null;
  status: DiscountStatus;
  /** Null or empty = any payment method qualifies. */
  paymentMethods?: PaymentPreference[] | null;
  /** Null or empty = both delivery and pickup qualify. */
  deliveryTypes?: DiscountDeliveryType[] | null;
  /**
   * The branches the discount runs at, as location ids; null/empty = all of them.
   *
   * Editable on merchant promotions. On a POS row it is READ-ONLY and reports what the POS itself is
   * running: Rhino's `descuento` list is per sucursal, so a descuento switched off at one branch comes
   * back scoped to the branches that still serve it.
   */
  locationIds?: string[] | null;
  /** Null = no weekly restriction. Evaluated live on every recompute, in `scheduleTimezone`. */
  scheduleHours?: DiscountScheduleHours | null;
  /** IANA zone the weekly windows are read in. Backend defaults to 'America/Montevideo'. */
  scheduleTimezone?: string | null;
  isSandbox: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DiscountUsageStats {
  redemptions: number;
  discountGiven: number;
  /** Total value of the orders that used the coupon, POST-discount — what was actually charged. */
  revenue: number;
  /**
   * Distinct customers who redeemed. Read against `redemptions` this separates a campaign that
   * reached many people from one a handful of regulars used repeatedly.
   */
  uniqueCustomers: number;
  averageOrderValue: number;
  averageDiscount: number;
  /** Null until the promotion has converted at least once. */
  firstRedeemedAt: string | null;
  lastRedeemedAt: string | null;
}

/** One row of the coupon's order list. The order detail dialog supplies everything beyond this. */
export interface DiscountRedemptionOrder {
  orderId: string;
  redeemedAt: string;
  /** What this order got off — the frozen per-redemption amount, not the coupon's current config. */
  discountAmount: number;
  orderTotal: number;
  /**
   * The order's status. Left as a plain string rather than importing `OrderStatus`: orders-client
   * already imports the coupon types for its `appliedDiscount` snapshot, and importing back would put
   * a cycle between the two clients for a value this module only ever passes through.
   */
  orderStatus: string;
  currency: string;
  deliveryType?: 'delivery' | 'pickup' | null;
  customerId: string;
  customerName?: string | null;
  customerPhone?: string | null;
}

export interface DiscountInput {
  /** Explicit `null` creates an automatic promotion; omit it on update (the kind is immutable). */
  code?: string | null;
  name?: string | null;
  description?: string | null;
  type?: DiscountType;
  value?: number;
  rewardItems?: DiscountRewardItem[] | null;
  quantityTiers?: DiscountQuantityTiers | null;
  minimumOrderAmount?: number | null;
  startsAt?: string | null;
  endsAt?: string | null;
  excludePromoItems?: boolean;
  maxUsesPerCustomer?: number | null;
  maxUsesTotal?: number | null;
  status?: DiscountStatus;
  paymentMethods?: PaymentPreference[] | null;
  deliveryTypes?: DiscountDeliveryType[] | null;
  /** Branch scope; `null` or `[]` means every branch. Rejected by the API on a POS-managed row. */
  locationIds?: string[] | null;
  scheduleHours?: DiscountScheduleHours | null;
  scheduleTimezone?: string | null;
}

/** Status pills. Both modes explicitly, per the theming rules — these carry meaning. */
export const DISCOUNT_STATUS_COLORS: Record<DiscountStatus, string> = {
  UNSUPPORTED: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  ACTIVE: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300',
  DISABLED: 'bg-muted text-muted-foreground',
};

export const DISCOUNT_TYPE_COLORS: Record<DiscountType, string> = {
  CART_PERCENT: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300',
  CART_FIXED: 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300',
  ITEM_DISCOUNT: 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300',
  ITEM_QUANTITY_TIERS: 'bg-teal-100 dark:bg-teal-900/40 text-teal-800 dark:text-teal-300',
};

/**
 * The ladder as one short phrase — "2+ → 10%, 3+ → 20%" or "2 → 10%, 3 → 20%".
 *
 * The `+` is the whole difference between the modes, and it is the part a merchant checks when they
 * reopen a promotion they configured last month.
 */
export function describeQuantityTiers(tiers: DiscountQuantityTiers): string {
  const suffix = tiers.mode === 'THRESHOLD' ? '+' : '';
  return [...tiers.tiers]
    .sort((a, b) => a.quantity - b.quantity)
    .map((tier) => `${tier.quantity}${suffix} → ${tier.discountPercent}%`)
    .join(', ');
}

/**
 * A coupon can read as ACTIVE and still not work: its window may not have opened yet, may have
 * closed, or its global cap may be spent. The list has to say which, or a merchant sees "activo"
 * next to a code customers are being told is invalid.
 */
export type DiscountEffectiveState = 'active' | 'disabled' | 'scheduled' | 'expired' | 'exhausted';

export function discountEffectiveState(
  coupon: Discount,
  stats?: DiscountUsageStats,
  now = new Date(),
): DiscountEffectiveState {
  if (coupon.status === 'DISABLED') return 'disabled';
  if (coupon.startsAt && now < new Date(coupon.startsAt)) return 'scheduled';
  if (coupon.endsAt && now > new Date(coupon.endsAt)) return 'expired';
  if (coupon.maxUsesTotal != null && stats && stats.redemptions >= coupon.maxUsesTotal) return 'exhausted';
  return 'active';
}

export const DISCOUNT_STATE_COLORS: Record<DiscountEffectiveState, string> = {
  active: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300',
  disabled: 'bg-muted text-muted-foreground',
  scheduled: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300',
  expired: 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300',
  exhausted: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300',
};

/**
 * POS integrations whose brands may see the coupons surface for now.
 *
 * Temporary UI-level rollout gate — nothing structural: the `/discounts` route, this client and the
 * backend all stay wired for every brand, so unblocking another POS is adding its type here (or
 * dropping the check at the two call sites in `App.tsx` / `app-header.tsx` to open it to everyone).
 */
const COUPON_ENABLED_POS_TYPES: readonly string[] = ['ambit', 'shopify'];

/**
 * Whether the coupons tab shows, given the brand's *enabled* POS provider types.
 *
 * An empty list means the brand has no POS at all, which also qualifies — there is no external
 * system whose pricing the discount would have to survive.
 */
export function discountsEnabledForPosTypes(enabledPosTypes: string[]): boolean {
  return enabledPosTypes.every((type) => COUPON_ENABLED_POS_TYPES.includes(type));
}

/**
 * Automatic promotions and coded coupons live in the same list; this is the only thing that tells
 * them apart. Use it rather than testing `code` inline so the convention stays in one place.
 */
export function isAutomaticPromotion(discount: Discount): boolean {
  return discount.code == null;
}

/**
 * The branches a discount runs at, named — or null when it runs at all of them.
 *
 * Names rather than ids, because a merchant reading "Solo: The Bar" learns something and a merchant
 * reading a uuid does not. An id with no matching branch is dropped: the branch was deleted after the
 * discount was scoped, and the next POS sync narrows the row anyway.
 */
export function discountBranchNames(
  discount: Pick<Discount, 'locationIds'>,
  locations: Array<{ id: string; name: string }>,
): string[] | null {
  if (!discount.locationIds?.length) return null;
  const namesById = new Map(locations.map((location) => [location.id, location.name]));
  return discount.locationIds.map((id) => namesById.get(id)).filter((name): name is string => !!name);
}

/** A discount the brand's POS authored: shown, never edited here, and kept in step by the menu sync. */
export function isPosDiscount(discount: Discount): boolean {
  return discount.source === 'POS';
}

/** A merchant-authored automatic promotion — codeless AND ours to edit. */
export function isMerchantPromotion(discount: Discount): boolean {
  return discount.code == null && discount.source !== 'POS';
}

export const discountsClient = {
  async list(): Promise<ApiResponse<Discount[]>> {
    return api<ApiResponse<Discount[]>>('/discounts');
  },

  async getById(discountId: string): Promise<ApiResponse<{ coupon: Discount; stats: DiscountUsageStats }>> {
    return api<ApiResponse<{ coupon: Discount; stats: DiscountUsageStats }>>(`/discounts/${discountId}`);
  },

  /** The coupon's conversions. Separate from `getById` so the list page's per-row stats stay cheap. */
  async listOrders(discountId: string, limit = 100): Promise<ApiResponse<DiscountRedemptionOrder[]>> {
    return api<ApiResponse<DiscountRedemptionOrder[]>>(`/discounts/${discountId}/orders?limit=${limit}`);
  },

  async create(input: DiscountInput): Promise<ApiResponse<Discount>> {
    return api<ApiResponse<Discount>>('/discounts', { method: 'POST', body: input });
  },

  async update(discountId: string, input: DiscountInput): Promise<ApiResponse<Discount>> {
    return api<ApiResponse<Discount>>(`/discounts/${discountId}`, { method: 'PATCH', body: input });
  },

  /** Kill a promotion early. Never deletes: confirmed orders must stay explicable. */
  async disable(discountId: string): Promise<ApiResponse<Discount>> {
    return api<ApiResponse<Discount>>(`/discounts/${discountId}`, { method: 'PATCH', body: { status: 'DISABLED' } });
  },

  async enable(discountId: string): Promise<ApiResponse<Discount>> {
    return api<ApiResponse<Discount>>(`/discounts/${discountId}`, { method: 'PATCH', body: { status: 'ACTIVE' } });
  },
};
